import { useMemo, useState } from "react";
import type { ChargeCode, LaborType } from "../../lib/timekeeping";
import {
  CERTIFICATION_TEXT,
  CERTIFICATION_TEXT_VERSION,
  LABOR_TYPE_HELP,
  SUPERVISOR_ATTESTATION_TEXT,
  SUPERVISOR_ATTESTATION_TEXT_VERSION,
  employeeHasCodeAccess,
} from "../../lib/timekeeping";

export function LaborTypeBadge({ type }: { type: LaborType }) {
  const colors: Record<LaborType, string> = {
    PT: "bg-navy/10 text-navy",
    ST: "bg-teal/15 text-teal",
    AT: "bg-gold/20 text-navy",
    LWOP: "bg-red-500/10 text-red-700",
  };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-xs font-semibold tracking-wide ${colors[type]}`}
      title={LABOR_TYPE_HELP[type].label}
    >
      {type}
    </span>
  );
}

export function LaborTypeHelpLink({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-xs text-teal underline underline-offset-2 hover:text-navy"
    >
      What do PT / ST / AT / LWOP mean?
    </button>
  );
}

export function LaborTypeHelpPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4">
      <div className="bg-white max-w-lg w-full border border-black/10 shadow-lg p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-xl text-navy">Labor types</h2>
          <button type="button" className="text-slate hover:text-navy text-sm" onClick={onClose}>
            Close
          </button>
        </div>
        <p className="text-sm text-slate">
          Each charge code sits under a labor bin. Leave balances move when you charge{" "}
          <strong>PT</strong> (Paid Time-Off) codes.
        </p>
        <ul className="space-y-3">
          {(Object.keys(LABOR_TYPE_HELP) as LaborType[]).map((k) => (
            <li key={k} className="border border-black/5 p-3">
              <div className="flex items-center gap-2">
                <LaborTypeBadge type={k} />
                <span className="font-semibold text-navy">{LABOR_TYPE_HELP[k].label}</span>
              </div>
              <p className="text-sm text-slate mt-1">{LABOR_TYPE_HELP[k].meaning}</p>
              <p className="text-xs text-slate mt-1">{LABOR_TYPE_HELP[k].leaveImpact}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function ChargeCodePicker({
  codes,
  employeeId,
  excludeCodes,
  onSelectAuthorized,
  onRequestAccess,
  onCancel,
}: {
  codes: ChargeCode[];
  employeeId: string;
  excludeCodes: string[];
  onSelectAuthorized: (code: ChargeCode) => void;
  onRequestAccess: (code: ChargeCode) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return codes
      .filter((c) => !excludeCodes.includes(c.code))
      .filter(
        (c) =>
          !needle ||
          c.code.toLowerCase().includes(needle) ||
          c.title.toLowerCase().includes(needle),
      );
  }, [codes, excludeCodes, q]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4">
      <div className="bg-white max-w-xl w-full border border-black/10 shadow-lg p-5 space-y-3 max-h-[80vh] flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-xl text-navy">Add charge code</h2>
          <button type="button" className="text-slate hover:text-navy text-sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
        <input
          autoFocus
          type="search"
          placeholder="Search by code or title…"
          className="w-full border border-black/10 px-3 py-2 text-sm"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <ul className="overflow-y-auto flex-1 divide-y divide-black/5 border border-black/5">
          {filtered.map((c) => {
            const authorized = employeeHasCodeAccess(c, employeeId);
            const closed = c.status === "closed";
            return (
              <li key={c.code}>
                <button
                  type="button"
                  disabled={closed}
                  className={`w-full text-left px-3 py-2.5 text-sm hover:bg-mist disabled:opacity-50 disabled:cursor-not-allowed ${
                    !authorized && !closed ? "bg-gold/5" : ""
                  }`}
                  onClick={() => {
                    if (closed) return;
                    if (authorized) onSelectAuthorized(c);
                    else onRequestAccess(c);
                  }}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-navy">{c.code}</span>
                    <LaborTypeBadge type={c.labor_type} />
                    {closed && (
                      <span className="text-xs text-red-700 font-medium">Closed</span>
                    )}
                    {c.is_unpaid_ot && (
                      <span className="text-xs text-navy font-medium">Unpaid OT (TTA)</span>
                    )}
                    {!authorized && !closed && (
                      <span className="text-xs text-gold font-medium">Request access</span>
                    )}
                  </div>
                  <div className="text-slate text-xs mt-0.5">{c.title}</div>
                  {c.description && (
                    <div className="text-slate text-xs mt-0.5">{c.description}</div>
                  )}
                  {(c.pop_start || c.pop_end) && (
                    <div className="text-slate text-xs">
                      POP: {c.pop_start ?? "—"} → {c.pop_end ?? "open"}
                    </div>
                  )}
                  <div className="text-slate text-xs">Manager: {c.manager_name}</div>
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="px-3 py-6 text-sm text-slate text-center">No matching codes.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

export function AccessRequestModal({
  code,
  reason,
  setReason,
  onSubmit,
  onCancel,
}: {
  code: ChargeCode;
  reason: string;
  setReason: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4">
      <form
        className="bg-white max-w-md w-full border border-black/10 shadow-lg p-5 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!reason.trim()) return;
          onSubmit();
        }}
      >
        <h2 className="font-display text-xl text-navy">Request charge code access</h2>
        <p className="text-sm text-slate">
          You are not authorized for <strong className="text-navy">{code.code}</strong> (
          {code.title}). Your request routes to{" "}
          <strong className="text-navy">{code.manager_name}</strong>, the code manager.
        </p>
        <label className="block text-sm font-medium text-navy">
          Why do you need access?
          <textarea
            className="mt-1 w-full border border-black/10 px-3 py-2 min-h-[100px] text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
        </label>
        <div className="flex gap-2 justify-end">
          <button type="button" className="px-3 py-2 text-sm border border-black/10" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 text-sm bg-navy text-white">
            Submit request
          </button>
        </div>
      </form>
    </div>
  );
}

export function ChangeNoteModal({
  date,
  oldHours,
  newHours,
  note,
  setNote,
  onConfirm,
  onCancel,
}: {
  date: string;
  oldHours: number;
  newHours: number;
  note: string;
  setNote: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4">
      <form
        className="bg-white max-w-md w-full border border-black/10 shadow-lg p-5 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!note.trim()) return;
          onConfirm();
        }}
      >
        <h2 className="font-display text-xl text-navy">Change note required</h2>
        <p className="text-sm text-slate">
          Changing {date} from <strong>{oldHours}</strong> to <strong>{newHours}</strong> hours
          requires a note for the audit trail.
        </p>
        <label className="block text-sm font-medium text-navy">
          Why is this changing?
          <textarea
            className="mt-1 w-full border border-black/10 px-3 py-2 min-h-[90px] text-sm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required
            autoFocus
          />
        </label>
        <div className="flex gap-2 justify-end">
          <button type="button" className="px-3 py-2 text-sm border border-black/10" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 text-sm bg-navy text-white">
            Save with note
          </button>
        </div>
      </form>
    </div>
  );
}

export function OverFloorBadge() {
  return (
    <span className="inline-block px-1.5 py-0.5 text-xs font-medium bg-gold/20 text-navy">
      Over floor
    </span>
  );
}

export function SubmitAttestationModal({
  total,
  required,
  onConfirm,
  onCancel,
}: {
  total: number;
  /** Period minimum hours; null = PTV (no minimum). */
  required: number | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [checked, setChecked] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4">
      <div className="bg-white max-w-md w-full border border-black/10 shadow-lg p-5 space-y-3">
        <h2 className="font-display text-xl text-navy">Certify &amp; submit</h2>
        <p className="text-sm text-slate">
          Period total <strong className="text-navy">{total}</strong> hrs
          {required != null ? (
            <>
              {" "}
              (minimum <strong className="text-navy">{required}</strong>
              {total > required + 0.001 ? "; above floor — supervisor will see Over floor" : ""})
            </>
          ) : (
            <> (no minimum — part-time variable)</>
          )}
          . Certification text version{" "}
          <code className="text-xs">{CERTIFICATION_TEXT_VERSION}</code>.
        </p>
        <label className="flex gap-2 text-sm text-navy items-start">
          <input
            type="checkbox"
            className="mt-1"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span>{CERTIFICATION_TEXT}</span>
        </label>
        <div className="flex gap-2 justify-end">
          <button type="button" className="px-3 py-2 text-sm border border-black/10" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!checked}
            className="px-4 py-2 text-sm bg-navy text-white disabled:opacity-40"
            onClick={onConfirm}
          >
            Submit timesheet
          </button>
        </div>
      </div>
    </div>
  );
}

export function SupervisorAttestationModal({
  employeeName,
  onConfirm,
  onCancel,
}: {
  employeeName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [checked, setChecked] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4">
      <div className="bg-white max-w-md w-full border border-black/10 shadow-lg p-5 space-y-3">
        <h2 className="font-display text-xl text-navy">Supervisor attestation</h2>
        <p className="text-sm text-slate">
          Approving timesheet for <strong className="text-navy">{employeeName}</strong>. Text version{" "}
          <code className="text-xs">{SUPERVISOR_ATTESTATION_TEXT_VERSION}</code>.
        </p>
        <label className="flex gap-2 text-sm text-navy items-start">
          <input
            type="checkbox"
            className="mt-1"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span>{SUPERVISOR_ATTESTATION_TEXT}</span>
        </label>
        <div className="flex gap-2 justify-end">
          <button type="button" className="px-3 py-2 text-sm border border-black/10" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!checked}
            className="px-4 py-2 text-sm bg-navy text-white disabled:opacity-40"
            onClick={onConfirm}
          >
            Approve timesheet
          </button>
        </div>
      </div>
    </div>
  );
}

export function StatusChip({ status }: { status: string }) {
  const tone =
    status === "Approved"
      ? "bg-teal/15 text-teal"
      : status === "Submitted"
        ? "bg-navy/10 text-navy"
        : status === "Rejected"
          ? "bg-red-500/10 text-red-700"
          : status === "Correction requested"
            ? "bg-gold/20 text-navy"
            : "bg-mist text-slate";
  return (
    <span className={`inline-block px-1.5 py-0.5 text-xs font-medium ${tone}`}>{status}</span>
  );
}
